import { Editor, type OnMount } from '@monaco-editor/react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './components/button';
import { LanguageSelect } from './components/language-select';
import { Loader } from './components/loader';
import { Output } from './components/output';
import {
  CODE_RUN_BUTTON_LABEL,
  DEFAULT_EDITOR_WIDTH,
  LOCAL_STORAGE_DEFAULT_LANGUAGE_ID,
  LOCAL_STORAGE_EDITOR_WIDTH,
  MAX_EDITOR_WIDTH,
  MIN_EDITOR_WIDTH,
} from './constants.ts';
import { executeCode } from './core/api';
import { useDebounce } from './hooks';
import type { EditorInstanceType, LanguageType } from './types.ts';
import {
  applyEditorOptions,
  bindCommentAndNextLine,
  bindDuplicateLineDown,
  bindRunShortcut,
  bindSaveShortcut,
  formatDocumentNow,
  getLanguageCodeStorageKey,
  restoreCodeByLanguageFromStorage,
  restoreLanguageFromStorage,
} from './utils';

export const App = () => {
  const editorReference = useRef<EditorInstanceType | null>(null);
  const outputReference = useRef<HTMLDivElement | null>(null);
  const containerReference = useRef<HTMLDivElement | null>(null);
  const [output, setOutput] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const [editorWidth, setEditorWidth] = useState(
    Number(localStorage.getItem(LOCAL_STORAGE_EDITOR_WIDTH) ?? DEFAULT_EDITOR_WIDTH),
  );
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(
    restoreLanguageFromStorage,
  );
  const [initialCodeByLanguage] = useState<Record<string, string>>(() =>
    restoreCodeByLanguageFromStorage(selectedLanguage),
  );
  const codeByLanguageReference = useRef({ ...initialCodeByLanguage });

  const handleRunCode = useCallback(async () => {
    if (isLoading) return;

    try {
      const code = editorReference?.current?.getValue?.();

      if (!code) return;

      setIsLoading(true);
      const response = await executeCode(code, selectedLanguage.id);
      const run = response?.data?.run;
      const executionOutput = run?.output || run?.stderr;
      const executionDetails = run?.message
        ? [
          `Execution stopped: ${run.message}`,
          run.wall_time != null
            ? `Elapsed time: ${(run.wall_time / 1000).toFixed(2)} s`
            : null,
          run.memory != null
            ? `Memory used: ${(run.memory / 1024 / 1024).toFixed(2)} MB`
            : null,
        ]
          .filter(Boolean)
          .join('\n')
        : null;

      setOutput(
        [executionOutput, executionDetails].filter(Boolean).join('\n\n') ||
          'Program finished with no output',
      );
    } catch (error) {
      if (error instanceof Error) {
        setOutput(error.message);
      } else {
        setOutput(String(error));
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, selectedLanguage.id]);

  const runRef = useRef(handleRunCode);
  useEffect(() => {
    runRef.current = handleRunCode;
  }, [handleRunCode]);

  const onEditorMount: OnMount = (editor, monaco) => {
    setIsEditorLoading(false);
    editorReference.current = editor;

    applyEditorOptions(editor);
    bindSaveShortcut(editor, monaco);
    bindRunShortcut(editor, monaco, runRef);
    bindCommentAndNextLine(editor, monaco);
    bindDuplicateLineDown(editor, monaco);
  };

  const handleCleanOutput = () => {
    setOutput(null);
  };

  const saveEditorCodeDebounced = useDebounce((languageId: string, value: string) => {
    localStorage.setItem(getLanguageCodeStorageKey(languageId), value);
  }, 1000);

  const handleEditorCodeChange = (value = '') => {
    const languageId = selectedLanguage.id;

    codeByLanguageReference.current[languageId] = value;
    saveEditorCodeDebounced(languageId, value);
  };

  const handleLanguageSelect = (language: LanguageType) => {
    const currentLanguageId = selectedLanguage.id;
    const currentCode =
      editorReference.current?.getValue() ??
      codeByLanguageReference.current[currentLanguageId] ??
      '';

    localStorage.setItem(getLanguageCodeStorageKey(currentLanguageId), currentCode);
    localStorage.setItem(LOCAL_STORAGE_DEFAULT_LANGUAGE_ID, language.id);
    codeByLanguageReference.current[currentLanguageId] = currentCode;
    setSelectedLanguage(language);
    requestAnimationFrame(() => editorReference.current?.focus());
  };

  const saveEditorWidthDebounced = useDebounce((width: number) => {
    localStorage.setItem(LOCAL_STORAGE_EDITOR_WIDTH, String(width));
  }, 300);

  const handleDrag = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    document.body.style.cursor = 'col-resize';
    const startX = event.clientX;
    const startWidth = editorWidth;

    let raf = 0;

    const onMove = (event: MouseEvent) => {
      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        const delta = event.clientX - startX;
        let newEditorWidth = startWidth + delta;

        // ограничения
        newEditorWidth = Math.max(
          MIN_EDITOR_WIDTH,
          Math.min(MAX_EDITOR_WIDTH, newEditorWidth),
        );

        setEditorWidth(newEditorWidth);
        saveEditorWidthDebounced(newEditorWidth);
      });
    };

    const onUp = () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className="bg-dark-400 flex min-h-screen overflow-hidden p-[10px]">
      <div className="m-[0_auto] flex w-full max-w-[1920px] flex-col gap-[20px]">
        <div className="flex flex-wrap gap-[10px] max-sm:justify-between">
          <Button onClick={handleRunCode} isDisabled={isEditorLoading}>
            {CODE_RUN_BUTTON_LABEL}
          </Button>

          <Button onClick={handleCleanOutput} isDisabled={isEditorLoading}>
            Clean output
          </Button>

          <Button
            onClick={() =>
              editorReference.current && formatDocumentNow(editorReference.current)
            }
            isDisabled={isEditorLoading || selectedLanguage.id === 'go'}
          >
            Prettify code
          </Button>

          <LanguageSelect
            selected={selectedLanguage}
            onSelect={handleLanguageSelect}
            isDisabled={isEditorLoading}
            className="z-10"
          />
        </div>

        <div
          className="flex min-h-0 flex-1 gap-[5px] max-lg:flex-col"
          ref={containerReference}
        >
          <div
            className={clsx(
              'h-full max-lg:!w-full',
              isEditorLoading && 'rounded-[8px] border-[1px]',
            )}
            style={{
              width: editorWidth,
              maxWidth: MAX_EDITOR_WIDTH,
            }}
          >
            <Editor
              defaultValue={
                codeByLanguageReference.current[selectedLanguage.id] ??
                selectedLanguage.defaultValue
              }
              defaultLanguage={selectedLanguage.id}
              path={selectedLanguage.id}
              onMount={onEditorMount}
              theme="vs-dark"
              loading={<Loader />}
              className="overflow-hidden rounded-[8px]"
              onChange={handleEditorCodeChange}
              language={selectedLanguage.id}
            />
          </div>

          <div
            onMouseDown={handleDrag}
            style={{
              cursor: 'col-resize',
            }}
            className="hover:bg-dark-300 w-[4px] rounded-[8px] bg-[#222] transition-colors"
          />

          <Output isLoading={isLoading} output={output} ref={outputReference} />
        </div>
      </div>
    </div>
  );
};
